<template>
  <Modal :value="value" @input="closeModal" width="lg">
    <div slot="header">
      Consulta avulsa de empresa
    </div>

    <div class="col-md-12">
      <input
          class="input form-control mb-2"
          @keypress.enter="find()"
          v-model="query.generic"
          placeholder="Pesquisar por CNPJ ou nome"
      >
      <search-icon @click="find()" class="icon icon-within"/>
    </div>

    <div class="col-md-12" v-for="company in companies" :key="company.id">
      <div class="accordion">
        <div class="accordion-header" @click="toggleAccordion(company.id)">
          <h4>{{ company.name }}-{{ company.cnpj }}</h4>
          <div>
            <span v-if="getServiceCount(company.id) > 0">{{ getServiceCount(company.id) }} órgão(s) selecionado(s)</span>
            <chevron-down-icon class="fa-angle-right" :class="{'rotate-90': activeAccordion === company.id }"/>
          </div>
        </div>

        <div v-if="activeAccordion === company.id" class="accordion-body">
          <div class="form-group col-md-6">
            <div class="form-check" v-for="service in getEnabledServicesForCompany(company)" :key="service.name">
              <input
                type="checkbox"
                :id="buildCheckboxId(company.id, service.name)"
                :checked="companyHaveService(company.id, service.name)"
                class="form-check-input"
                @change="updateSelectedServices(company.id, service.name, $event)"
              >
              <label :for="buildCheckboxId(company.id, service.name)" class="form-check-label">
                {{ service.friendlyName || service.name }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <hr>
    <div v-if="serviceSelected" class="summary-section">
      <small class="bold-text">Resumo de consultas: </small>
      <p v-for="([company, amountService]) in listCompanyServices" :key="company.id">
        <small v-if="amountService > 0">{{ company.cnpj }} - {{ amountService }} órgão(s) selecionado(s)</small>
      </p>
    </div>

    <button class="btn btn-primary float-right" :disabled="!serviceSelected || accountIsClosed"
            @click="sendConsultRequests">
      Consultar
    </button>
  </Modal>
</template>

<script>
import GetAccount from '@/mixins/GetAccount'
import handleSearchable from '@/models/handleSearchable'
import Modal from '@/components/shares/specialTag/Modal'
import ModelMapper from '@/mixins/ModelMapper'

import { Converter } from '@/models/converter'
import { ChevronDownIcon, SearchIcon } from 'vue-feather-icons'
import { COMPANY_STATUS } from '@/utils/companyStatus'

const toCamelcase = string => {
  return new Converter({
    converterName: 'camelcase',
    data: string
  }).perform()
}

export default {
  components: {
    Modal,
    ChevronDownIcon,
    SearchIcon
  },
  computed: {
    companies() {
      return this.getModels || []
    },
    listCompanyServices() {
      return Object.entries(this.selectedServices).map(([companyId, services]) => {
        const company = this.cacheCompanies[companyId]
        return [company, services.length]
      })
    },
    serviceSelected() {
      return Object.values(this.selectedServices).some(list => list.length > 0)
    },
  },
  methods: {
    closeModal() {
      this.$emit('input', false)
    },
    fetchScrapingServices() {
      const params = {
        meta: false,
        per_page: 10000,
        query: {
          enable: true,
          app_name: this.appName
        }
      }

      return this.axios.get('maestro/scraping_services', { params })
        .then(response => this.scrapingServices = toCamelcase(response.data))
        .then(() => this.scrapingServiceLoaded = true)
        .catch(e => this.$notify.addError(e))
    },
    buildParams(pagination) {
      if (this.query.generic)
        this.query.generic = handleSearchable(this.query.generic)

      return ModelMapper.methods.buildParams.call(this, pagination)
    },
    find() {
      this.asyncTemplateSelect()
    },
    getServiceCount(companyId) {
      const list = this.selectedServices[companyId]
      return list && list.length || 0
    },
    buildCheckboxId(companyId, serviceName) {
      return `${companyId}-${serviceName}-checkbox`
    },
    getEnabledServicesForCompany(company) {
      if (!company.services) {
        return []
      }

      const enabledServiceNames = Object.entries(company.services)
        .filter(([, config]) => config.enable)
        .map(([name]) => name)

      return this.scrapingServices.filter(service =>
        enabledServiceNames.includes(service.name)
      )
    },
    sendConsultRequests() {
      let delay = 0
      const consults = []

      Object.entries(this.selectedServices).map(([companyId, services]) => {
        const company = this.cacheCompanies[companyId]
        services.forEach(scrapingServiceName => consults.push({
          companyId: parseInt(companyId),
          scrapingServiceName,
          cnpj: company?.cnpj
        }))
      })

      const message = consults.length > 1 ? `Tem certeza de que deseja agendar ${consults.length} consultas?` :
                                            'Tem certeza de que deseja agendar 1 consulta?'
      if (!confirm(message))
        return

      consults.forEach(consult => {
        setTimeout(() => this.sendConsultByService(consult), delay)
        delay += 800
      })

      this.closeModal()
    },
    sendConsultByService(consult) {
      const scrapingService = this.scrapingServices.find(s => s.name === consult.scrapingServiceName)
      const scrapingServiceLabel = scrapingService?.friendlyName || scrapingService?.name || consult.scrapingServiceName
      const data = {
        scraping_service_name: consult.scrapingServiceName,
        company_id: consult.companyId,
      }

      this.openGlobalLoad()
      this.axios
        .post('cm-embarcador/consults', data)
        .then(() => this.$notify.add(`A consulta ${scrapingServiceLabel} para a empresa ${consult.cnpj} foi agendada`))
        .catch(e => {
          if (e.errors && e.errors[0].includes('credencial')) {
            this.$notify.addError(`Credenciais não encontradas para a empresa ${consult.cnpj} no serviço ${scrapingServiceLabel}`)
          } else {
            this.$notify.addError(e)
          }
        })
        .finally(() => this.closeGlobalLoad())
    },
    toggleAccordion(id) {
      if (this.activeAccordion === id)
        this.activeAccordion = null
      else
        this.activeAccordion = id
    },
    companyHaveService(companyId, scrapingServiceName) {
      return this.selectedServices[companyId] && this.selectedServices[companyId].includes(scrapingServiceName)
    },
    updateSelectedServices(companyId, scrapingServiceName, event) {
      if (!Array.isArray(this.selectedServices[companyId]))
        this.$set(this.selectedServices, companyId, [])

      if (event && event.target.checked) {
        this.addCacheCompanies(companyId)
        return this.selectedServices[companyId].push(scrapingServiceName)
      }

      const index = this.selectedServices[companyId].indexOf(scrapingServiceName)
      this.selectedServices[companyId].splice(index, 1)
    },
    addCacheCompanies(companyId) {
      const company = this.companies.find(c => c.id === companyId)
      this.cacheCompanies[companyId] = { id: company.id, cnpj: company.cnpj }
    },
    clearSelections() {
      this.selectedServices = {}
      this.cacheCompanies = {}
      this.query.generic = ''
      this.activeAccordion = null
      this.models = []
    }
  },
  mixins: [GetAccount, ModelMapper],
  name: 'CompanyRequestModal',
  created() {
    this.$set(this.query, 'entityStatusId', COMPANY_STATUS.ENABLED)
  },
  data: () => ({
    activeAccordion: null,
    cacheCompanies: {},
    selectedServices: {},
    nameModel: 'Company',
    scrapingServices: [],
    scrapingServiceLoaded: false
  }),
  props: {
    value: { default: false, type: Boolean },
    appName: { required: true, type: String },
  },
  watch: {
    value(newValue) {
      if (newValue && !this.scrapingServiceLoaded)
        this.fetchScrapingServices()

      if (!newValue)
        this.clearSelections()
    }
  }
}
</script>

<style scoped>
.accordion {
  border: 1px solid var(--border-color);
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
}

.accordion-header {
  padding: 1rem;
  background-color: var(--light-gray);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  color: var(--secondary-text-color);
}

.accordion-header h4 {
  color: var(--primary-text-color);
  margin: 0;
}

.accordion-header:hover {
  background-color: var(--btn-hover-bg);
}

.accordion-body {
  padding: 1rem;
  background-color: var(--white-bg);
}

.form-check-label {
  color: var(--primary-text-color);
  margin-left: 0.5rem;
}

.summary-section {
  color: var(--primary-text-color);
}

.summary-section p {
  color: var(--primary-text-color);
  margin: 0.25rem 0;
}

.summary-section small {
  color: var(--secondary-text-color);
}
</style>
